import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { RouterModule } from '@angular/router';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { ERROR_ANIMATION } from '../../../core/animations';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

export interface BirthdayForm {
  day: FormControl<string | null>;
  year: FormControl<string | null>;
  month: FormControl<string | null>;
}

@Component({
  selector: 'app-birthday-input',
  imports: [AlpitranslatePipe, ReactiveFormsModule, RouterModule, LocalizeRouterModule],
  animations: [ERROR_ANIMATION],
  templateUrl: './birthday-input.component.html',
  styleUrl: './birthday-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BirthdayInputComponent implements OnInit, OnChanges {
  private _givenBirthday: string;
  @Input() set givenBirthday(val: string | null) {
    if (val) {
      this._givenBirthday = val;
      if (this.birthdayForm && this._givenBirthday.match(/\d{4}-\d{2}-\d{2}/).length > 0) {
        const _date = new Date(val);
        this.birthdayForm.patchValue(
          {
            year: _date.getFullYear().toString(),
            month: this.prefixNumber(_date.getMonth() + 1),
            day: this.prefixNumber(_date.getDate())
          },
          { emitEvent: false }
        );
      }
    }
  }
  @Input() minDate: { day: number; month: number; year: number };
  @Input() maxDate: { day: number; month: number; year: number };
  @Input() required: boolean;
  @Input() formControlToSet: UntypedFormControl;
  @Input() shopID: string;
  @Input() usesAgeIntervalLength: boolean;

  @ViewChild('day', { static: true }) private dayInput: ElementRef;
  @ViewChild('month', { static: true }) private monthInput: ElementRef;
  @ViewChild('year', { static: true }) private yearInput: ElementRef;

  private preventArrowKeyDeselect = false;

  birthdayForm: FormGroup<BirthdayForm>;
  separator: string;
  dayPlaceholder: string;
  monthPlaceholder: string;
  yearPlaceholder: string;
  isBirthday = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    public translateService: TranslateService,
    private crowdinService: CrowdinTranslationService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.crowdinService.watchLangChange({ loaderMap: loaderMap, destroyRef: this.destroyRef });
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
    });
    const _date = new Date(this._givenBirthday);
    if (isNaN(_date.getTime())) {
      this._givenBirthday = null;
    }
    this.birthdayForm = this.fb.group(
      {
        day: [
          this._givenBirthday ? this.prefixNumber(_date.getDate()) : '',
          this.required
            ? [Validators.required, Validators.pattern(/^([1-9]|0[1-9]|1[0-9]|2[0-9]|3[0-1])$/)]
            : [Validators.pattern(/^([1-9]|0[1-9]|1[0-9]|2[0-9]|3[0-1])$/)]
        ],
        month: [
          this._givenBirthday ? this.prefixNumber(_date.getMonth() + 1) : '',
          this.required
            ? [Validators.required, Validators.pattern(/^([1-9]|0[1-9]|1[0-2])$/)]
            : [Validators.pattern(/^([1-9]|0[1-9]|1[0-2])$/)]
        ],
        year: [
          this._givenBirthday ? _date.getFullYear().toString() : '',
          this.required
            ? [Validators.required, Validators.pattern(/^(19|20)\d\d$/)]
            : [Validators.pattern(/^(19|20)\d\d$/)]
        ]
      },
      { validators: birthdayDateValidator(this.minDate, this.maxDate, this.usesAgeIntervalLength) }
    );

    this.birthdayForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.birthdayForm.valid)
      )
      .subscribe(date => {
        if (this.formControlToSet) {
          this.formControlToSet.setValue(date);
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const birthday = new Date(now.getFullYear(), +date.month - 1, +date.day, 0, 0, 0, 0);
          // 86400000 == 1 day in milliseconds
          if (
            birthday.getTime() - 86400000 * 2 <= now.getTime() &&
            birthday.getTime() + 86400000 * 2 >= now.getTime()
          ) {
            this.isBirthday = true;
            // Todo: set in promocode BIRTHDAY if cart has no promocode right now.
          } else {
            this.isBirthday = false;
          }
          this.cdr.markForCheck();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['usesAgeIntervalLength'] && changes['usesAgeIntervalLength']?.firstChange === false) {
      if (changes['usesAgeIntervalLength'].currentValue === true) {
        this.birthdayForm.setValidators(birthdayDateValidator(this.minDate, this.maxDate, true));
        this.birthdayForm.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  selectFirst($event) {
    const target = $event.target.getElementsByTagName('input')?.[0];
    if (target) {
      target.click();
    }
  }

  keyDownDay($event) {
    if ($event.keyCode === 39 && $event.target.selectionStart === $event.target.value.length) {
      // arrow right
      this.monthInput.nativeElement.click();
      this.preventArrowKeyDeselect = true;
    }
  }
  keyDownMonth($event) {
    if ($event.keyCode === 39 && $event.target.selectionStart === $event.target.value.length) {
      // arrow right
      this.yearInput.nativeElement.click();
      this.preventArrowKeyDeselect = true;
    }
    if ($event.keyCode === 37 && $event.target.selectionStart === 0 && $event.target.selectionEnd === 0) {
      this.dayInput.nativeElement.click();
      this.preventArrowKeyDeselect = true;
    }
  }
  keyDownYear($event) {
    if ($event.keyCode === 37 && $event.target.selectionStart === 0 && $event.target.selectionEnd === 0) {
      // arrow right
      this.monthInput.nativeElement.click();
      this.preventArrowKeyDeselect = true;
    }
  }

  continueOnDay($event) {
    if (this.preventArrowKeyDeselect) {
      $event.target.select();
      this.preventArrowKeyDeselect = false;
    }
    // tab, shift, ctrl, alt, arrow keys
    if ([9, 16, 17, 18, 37, 38, 39, 40].indexOf($event.keyCode) === -1) {
      if (this.birthdayForm.get('day').value.toString().length === 2) {
        this.monthInput.nativeElement.click();
      }
    }
  }

  continueOnMonth($event) {
    if (this.preventArrowKeyDeselect) {
      $event.target.select();
      this.preventArrowKeyDeselect = false;
    }
    if ([9, 16, 17, 18, 37, 38, 39, 40].indexOf($event.keyCode) === -1) {
      if (this.birthdayForm.get('month').value.toString().length === 2) {
        this.yearInput.nativeElement.click();
      }
    }
  }

  continueOnYear($event) {
    if (this.preventArrowKeyDeselect) {
      $event.target.select();
      this.preventArrowKeyDeselect = false;
    }
  }

  prefixNumber(num: number | string): string {
    if (typeof num === 'string') {
      num = parseInt(num);
    }
    if (num) {
      return num < 10 && !num.toString().startsWith('0') ? `0${num}` : num.toString();
    }
    return '';
  }
}

export function birthdayDateValidator(
  minDate?: { day: number; month: number; year: number },
  maxDate?: { day: number; month: number; year: number },
  usesAgeIntervalLength = false
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentDate = new Date();
    const year = +control.get('year').value;
    const month = +control.get('month').value;
    const day = +control.get('day').value;
    if (year === 0 && month === 0 && day === 0) {
      return null;
    }
    // validate year
    if (year < 1923) {
      return { yearTooOld: year };
    }
    if (year > 2023) {
      return { yearTooNew: year };
    }

    // validate month
    if (month > 12) {
      return { monthTooHigh: month };
    }

    // validate day (based on month)
    if (day > 28) {
      if (month === 2) {
        const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || (year % 100 === 0 && year % 400 === 0));
        if ((isLeapYear && day > 29) || !isLeapYear) {
          return { dayOfMonthInvalid: day };
        }
      } else if ([4, 6, 9, 11].indexOf(month) !== -1) {
        if (day > 30) {
          return { dayOfMonthInvalid: day };
        }
      } else {
        if (day > 31) {
          return { dayOfMonthInvalid: day };
        }
      }
    }

    // seems to be a date. Is it in the future?
    if (new Date(year, month - 1, day) > currentDate) {
      return { dateInTheFuture: true };
    }

    // last but not least: check bounds of the persons age interval or ignore it on ski-length shops (check shopID: 2355)
    if (minDate && !usesAgeIntervalLength) {
      if (new Date(year, month - 1, day) < new Date(minDate.year, minDate.month - 1, minDate.day)) {
        return { dateTooOld: true };
      }
    }
    if (maxDate && !usesAgeIntervalLength) {
      if (new Date(year, month - 1, day) > new Date(maxDate.year, maxDate.month - 1, maxDate.day)) {
        return { dateTooNew: true };
      }
    }
    // everything is okay
    return null;
  };
}
